import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { fromMinorUnits, ROLES, type Role } from '@/features/hierarchy/hierarchy'
import type { DateRange, GameBucket, PlTypeName, PnlRow, Sport } from '@/features/reports/types'

const DEFAULT_PAGE_SIZE = 20

function fromMinorUnitsSigned(minor: string | null | undefined): number {
  if (minor == null || minor === '') return 0
  const n = Number(minor)
  return Number.isFinite(n) ? n / 100 : 0
}

export type PlStatementQuery = {
  range: DateRange
  page?: number
  pageSize?: number
  rootId?: string
  memberIds?: string[]
  sports?: Sport[]
  games?: GameBucket[]
  q?: string
  /** Company Master Triple Master filter — Lab `tms`. */
  tms?: string
  /** Hierarchy drill-down — Lab `name: { _id, accessLevel }`. */
  name?: { _id: string; accessLevel?: string }
  /** Lab `Game/getGames` `_id`, or omit for all products. */
  game?: string
  /** Lab `Account/getSubGames` name, or omit for every sub-game. */
  subGame?: string
  /** Lab `Account/getEventsForPLL` event name, or omit for every event. */
  eventName?: string
  /** Lab Select Type → `typeName` (Match Odds / Bookmaker / Session / …). */
  typeName?: PlTypeName
  /** Lab staff Market access — rewrites typeName server-side when set. */
  bookmakerSessionFlag?: 'all' | 'onlyBookmaker' | 'onlySession' | 'bookmakerSession'
}

export type PlStatementRowView = Omit<PnlRow, 'marketId' | 'sport'> & {
  marketId: string
  /** Lab SubGame label (sport name, Live Casino table, or Card Game). */
  sport: string
  memberName: string
  splitNames: Partial<Record<Role, string>>
}

export type PlStatementResult = {
  rows: PlStatementRowView[]
  splitRoles: Role[]
  total: number
  page: number
  pageSize: number
  netPnl: number
  asOf?: string
  martStale?: boolean
  unavailable?: { code: string; reason: string; trackedBy?: string }
}

const SPORTS: readonly Sport[] = [
  'Cricket',
  'Soccer',
  'Tennis',
  'Horse Racing',
  'Greyhound Racing',
  'Casino',
]

const GAMES: readonly GameBucket[] = [
  'SportsBook',
  'SportsBook - Bookmaker',
  'Fancy',
  'Racing',
  'Live Casino',
  'Crash Game',
  'Card Game',
  'Credit Limit',
]

const ROLE_SET = new Set<string>(ROLES)

export function asSport(raw: string): Sport {
  return SPORTS.includes(raw as Sport) ? (raw as Sport) : 'Cricket'
}

export function asGame(raw: string): GameBucket {
  return GAMES.includes(raw as GameBucket) ? (raw as GameBucket) : 'SportsBook'
}

/**
 * Lab PL “SubGame” cell. Live Casino / Card Game must not run through
 * `asSport(… || 'Cricket')` — Lab Live ROULETTE (and SoftGamings empty stamps)
 * are not Sport enums, so they were painted as Cricket.
 */
export function plStatementSubGame(
  game: GameBucket,
  rawSubGame: string | null | undefined,
): string {
  const raw = String(rawSubGame ?? '').trim()
  if (game === 'Live Casino' || game === 'Crash Game') {
    // Sportsbook sport stamps on casino settles are wrong; treat as missing.
    if (
      !raw ||
      raw === 'None' ||
      raw === 'Cricket' ||
      raw === 'Soccer' ||
      raw === 'Tennis'
    ) {
      return 'Casino'
    }
    return raw
  }
  if (game === 'Card Game') {
    return raw && raw !== 'None' ? raw : 'Card Game'
  }
  if (game === 'Racing') {
    return raw && raw !== 'None' ? raw : 'Horse Racing'
  }
  return asSport(raw || 'Cricket')
}

export function asRole(raw: string): Role | null {
  return ROLE_SET.has(raw) ? (raw as Role) : null
}

export function emptyPlStatement(query: PlStatementQuery): PlStatementResult {
  const pageSize = query.pageSize ?? DEFAULT_PAGE_SIZE
  return {
    rows: [],
    splitRoles: [],
    total: 0,
    page: query.page ?? 1,
    pageSize,
    netPnl: 0,
  }
}

export function unavailablePlStatement(
  query: PlStatementQuery,
  reason?: string,
): PlStatementResult {
  return {
    ...emptyPlStatement(query),
    unavailable: {
      code: 'no_pl_statement_source',
      reason:
        reason ??
        'Event-level P&L needs CM-API POST /v1/account/getDownlineAccountsData. It is not reachable, so this console will not invent settled rows.',
      trackedBy: 'cm-ui-reports:pl-statement',
    },
  }
}

/** Lab `mapAccountsAPI` party — amounts are already major units. */
export type DownlineAccountParty = {
  _id?: string
  name: string
  isVirtual?: boolean
  userAccessLevel?: string
  amount: { winAmount?: number; loseAmount?: number; netAmount: number }
  commission?: { turnOver?: number; netCommission?: number }
}

export type DownlineAccountRow = {
  _id: string
  createdAt: string
  /** Settlement currency from CM-API (INR / POINTS / POINT@1). */
  currencyType?: string
  /** Lab Invalid Bets — Account Statement Status / event prefix. */
  isInvalid?: boolean
  isRollback?: boolean
  /** Lab Invalidate P&L flags — list still shows these rows. */
  isPNLInvalid?: boolean
  isPNLRollBack?: boolean
  gameData: {
    eventName?: string
    gameName?: string
    matchName?: string
    subGame?: string
    betCount?: number
    isBookmaker?: boolean
    isFancy?: boolean
    isCardGame?: boolean
    marketId?: string
    eventId?: string
  }
  playerData: DownlineAccountParty
  masterData?: DownlineAccountParty
  superMasterData?: DownlineAccountParty
  doubleMasterData?: DownlineAccountParty
  tripleMasterData?: DownlineAccountParty
  companyMasterData?: DownlineAccountParty
  /** Account Statement balance stamps (Give/Take). */
  previousBalance?: number
  nextBalance?: number
  exposure?: number
  currBalance?: number
}

export type GetDownlineAccountsDataResponse = {
  value: boolean
  data?: {
    maxRow?: number
    result?: DownlineAccountRow[]
    totalCount?: number
    net?: number
  }
}

export type GetDownlineAccountsSumOnlyResponse = {
  value: boolean
  data?: {
    masterNetAmountSum?: number
    superMasterNetAmountSum?: number
    doubleMasterNetAmountSum?: number
    tripleMasterNetAmountSum?: number
    companyMasterNetAmountSum?: number
    totalRecords?: number
  }
}

const GAME_TYPE: Record<string, string> = {
  'SportsBook - Bookmaker': 'Bookmaker',
  Fancy: 'Session',
}

/**
 * Lab PL footer: today window (Mongo/PG) or historical BQ ≤93 days
 * (`PLStatementCtrl` + `getTotalNetAmountNew` / SumOnly).
 */
export function canLoadSumOnly(range: { from: string; to: string }, _now = new Date()): boolean {
  const from = Date.parse(`${range.from.slice(0, 10)}T00:00:00Z`)
  const to = Date.parse(`${range.to.slice(0, 10)}T00:00:00Z`)
  const day = 24 * 60 * 60 * 1000
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return false
  const diffDays = Math.ceil(Math.abs(to - from) / day)
  return diffDays <= 93
}

/** Lab toastr when range span exceeds 93 days. */
export function plStatementRangeError(range: { from: string; to: string }): string | null {
  const from = Date.parse(`${range.from.slice(0, 10)}T00:00:00Z`)
  const to = Date.parse(`${range.to.slice(0, 10)}T00:00:00Z`)
  const day = 24 * 60 * 60 * 1000
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return null
  const diffDays = Math.ceil(Math.abs(to - from) / day)
  if (diffDays > 93) {
    return 'The date you have selected exceeds 93 days. Please try again.'
  }
  return null
}

/**
 * Lab `Account/getDownlineAccountsData` POST body — same shape Backend-UI sends
 * via hierarchy `POST /v1/account/getDownlineAccountsData`.
 */
export function liveBody(query: PlStatementQuery, page: number, pageSize: number) {
  const body: Record<string, unknown> = {
    fromDate: query.range.from.slice(0, 10),
    toDate: query.range.to.slice(0, 10),
    page,
    pageSize,
  }
  if (query.q?.trim()) body.q = query.q.trim()
  if (query.tms) body.tms = query.tms
  if (query.name?._id) body.name = query.name
  if (query.memberIds?.length) body.playerName = query.memberIds
  if (query.subGame) body.subGame = query.subGame
  else if (query.sports?.length === 1) body.subGame = query.sports[0]
  if (query.game && query.game !== 'all') body.game = query.game
  if (query.eventName) body.eventName = query.eventName
  if (query.typeName) body.typeName = query.typeName
  else if (query.games?.length === 1) {
    const mapped = GAME_TYPE[query.games[0]!]
    if (mapped) body.typeName = mapped
  }
  // Lab Account.js — staff Market access rewrites typeName before SQL.
  if (query.bookmakerSessionFlag && query.bookmakerSessionFlag !== 'all') {
    body.bookmakerSessionFlag = query.bookmakerSessionFlag
  }
  return body
}

/** Paged PL Statement rows — CM-API `POST /v1/account/getDownlineAccountsData`. */
export async function getDownlineAccountsData(
  body: Record<string, unknown>,
): Promise<GetDownlineAccountsDataResponse> {
  return cmApi.getDownlineAccountsData(authHeaders(), body)
}

/** Recent role-net footer — CM-API `POST /v1/account/getDownlineAccountsSumOnly`. */
export async function getDownlineAccountsSumOnly(
  body: Record<string, unknown>,
): Promise<GetDownlineAccountsSumOnlyResponse> {
  return cmApi.getDownlineAccountsSumOnly(authHeaders(), body)
}

export function netFromDownlineSumOnly(
  response: GetDownlineAccountsSumOnlyResponse | null | undefined,
): number | undefined {
  if (!response?.value || !response.data) return undefined
  const values = [
    response.data.masterNetAmountSum,
    response.data.superMasterNetAmountSum,
    response.data.doubleMasterNetAmountSum,
    response.data.tripleMasterNetAmountSum,
    response.data.companyMasterNetAmountSum,
  ].filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
  return values.length ? values.reduce((sum, value) => sum + value, 0) : undefined
}

const LAB_PARTY: { key: keyof DownlineAccountRow; role: Role }[] = [
  { key: 'masterData', role: 'master' },
  { key: 'superMasterData', role: 'super-master' },
  { key: 'doubleMasterData', role: 'double-master' },
  { key: 'tripleMasterData', role: 'triple-master' },
  { key: 'companyMasterData', role: 'company-master' },
]

const LAB_ACCESS: Record<Role, string> = {
  'company-master': 'companyMaster',
  'triple-master': 'tripleMaster',
  'double-master': 'doubleMaster',
  'super-master': 'superMaster',
  master: 'master',
  player: 'player',
}

export function labAccessLevel(role: Role): string {
  return LAB_ACCESS[role]
}

function gameFromDownline(row: DownlineAccountRow): GameBucket {
  const g = row.gameData
  if (g.isCardGame) return 'Card Game'
  if (g.isFancy) return 'Fancy'
  if (g.isBookmaker) return 'SportsBook - Bookmaker'
  return asGame(g.gameName ?? 'SportsBook')
}

/**
 * POINTS ÷100 (Pts) for Bookmaker, Betfair Match Odds, Racing (HR/GHR),
 * Fancy / line-session, Live Casino, and Card Game (settlement stores INR
 * majors; 1 pt = ₹100).
 */
export function plDisplayCurrencyType(
  currencyType: string | undefined,
  opts: {
    isBookmaker?: boolean
    isFancy?: boolean
    isCardGame?: boolean
    gameName?: string
    marketName?: string
  },
): string | undefined {
  const raw = currencyType?.trim() || undefined
  if (!raw) return undefined
  if (opts.isBookmaker || opts.isFancy || opts.isCardGame) return raw
  const gameName = String(opts.gameName ?? '').trim() || 'SportsBook'
  if (
    gameName === 'Live Casino' ||
    gameName === 'Crash Game' ||
    gameName === 'Card Game' ||
    gameName === 'Racing'
  ) {
    return raw
  }
  if (gameName !== 'SportsBook') return undefined
  return raw
}

export function toPlStatementRowFromDownline(row: DownlineAccountRow): PlStatementRowView {
  const memberPnl = row.playerData.amount.netAmount
  const split: PnlRow['split'] = {}
  const splitNames: Partial<Record<Role, string>> = {}
  for (const { key, role } of LAB_PARTY) {
    const party = row[key]
    if (!party || typeof party !== 'object' || !('amount' in party)) continue
    split[role] = party.amount.netAmount
    if (party.name && party.name !== 'NA') splitNames[role] = party.name
  }
  const marketId = row.gameData.marketId?.trim() || row._id
  const g = row.gameData
  const game = gameFromDownline(row)
  const currencyType = plDisplayCurrencyType(row.currencyType, {
    isBookmaker: g.isBookmaker,
    isFancy: g.isFancy,
    isCardGame: g.isCardGame,
    gameName: g.gameName,
    marketName: g.matchName,
  })
  return {
    id: row._id,
    settledAt: row.createdAt,
    memberId: row.playerData._id ?? '',
    sport: plStatementSubGame(game, row.gameData.subGame),
    game,
    marketId,
    eventId: row.gameData.eventId?.trim() || marketId,
    eventName: row.gameData.eventName ?? '',
    marketName: row.gameData.matchName ?? '',
    memberPnl,
    commission: row.playerData.commission?.netCommission ?? 0,
    netAmount: memberPnl,
    split,
    betCount: row.gameData.betCount ?? 1,
    turnover: row.playerData.commission?.turnOver ?? 0,
    currencyType,
    memberName: row.playerData.name,
    splitNames,
    isPNLInvalid: Boolean(row.isPNLInvalid),
    isPNLRollBack: Boolean(row.isPNLRollBack),
    isInvalid: Boolean(row.isInvalid),
    isRollback: Boolean(row.isRollback),
  }
}

export function fromDownlineAccountsData(
  live: GetDownlineAccountsDataResponse,
  query: PlStatementQuery,
  sumOnly?: GetDownlineAccountsSumOnlyResponse | null,
): PlStatementResult {
  const pageSize = live.data?.maxRow ?? query.pageSize ?? DEFAULT_PAGE_SIZE
  if (!live.value) return emptyPlStatement({ ...query, pageSize })
  const raw = live.data?.result ?? []
  const rows = raw.map(toPlStatementRowFromDownline)
  const splitRoles = LAB_PARTY.filter(({ key }) => raw.some((row) => Boolean(row[key]))).map(
    ({ role }) => role,
  )
  const pageNet = rows.reduce((sum, row) => sum - row.memberPnl, 0)
  const roleNet = netFromDownlineSumOnly(sumOnly)
  const sumTotal = sumOnly?.value ? sumOnly.data?.totalRecords : undefined
  return {
    rows,
    splitRoles,
    total:
      typeof sumTotal === 'number' && sumTotal > 0
        ? sumTotal
        : (live.data?.totalCount ?? raw.length),
    page: query.page ?? 1,
    pageSize,
    netPnl: roleNet ?? (typeof live.data?.net === 'number' ? live.data.net : pageNet),
  }
}

export type GetEventsForPllResponse = {
  value: boolean
  data: string[]
}

export function namesFromGetEventsForPll(response: GetEventsForPllResponse): string[] {
  if (!response.value) return []
  return (response.data ?? []).filter(
    (name): name is string => typeof name === 'string' && Boolean(name.trim()),
  )
}

/** Analytics envelope row mapping — kept for unit tests / future mart route. */
export type AnalyticsPlSplitCell = { name?: string; netMinor: string }

export type AnalyticsPlStatementRow = {
  id: string
  settledAt: string
  memberId: string
  name: string
  sport: string
  game: string
  marketId: string
  eventId: string
  eventName: string
  marketName: string
  turnoverMinor: string
  memberNetMinor: string
  bookNetMinor: string
  commissionMinor?: string
  betCount: number
  split?: Record<string, AnalyticsPlSplitCell>
}

export type AnalyticsPlStatementPage = {
  asOf?: string
  martStale?: boolean
  page?: number
  size?: number
  total?: number
  netMinor?: string
  splitRoles?: string[]
  rows: AnalyticsPlStatementRow[]
}

function splitFromAnalytics(split: AnalyticsPlStatementRow['split']): {
  amounts: PnlRow['split']
  names: Partial<Record<Role, string>>
} {
  const amounts: PnlRow['split'] = {}
  const names: Partial<Record<Role, string>> = {}
  if (!split) return { amounts, names }
  for (const [key, cell] of Object.entries(split)) {
    const role = asRole(key)
    if (!role || !cell) continue
    amounts[role] = fromMinorUnitsSigned(cell.netMinor)
    if (cell.name) names[role] = cell.name
  }
  return { amounts, names }
}

export function toPlStatementRow(row: AnalyticsPlStatementRow): PlStatementRowView {
  const memberPnl = fromMinorUnitsSigned(row.memberNetMinor)
  const bookNet = fromMinorUnitsSigned(row.bookNetMinor)
  const commission = row.commissionMinor ? fromMinorUnits(row.commissionMinor) : 0
  const { amounts, names } = splitFromAnalytics(row.split)
  const game = asGame(row.game)
  return {
    id: row.id,
    settledAt: row.settledAt,
    memberId: row.memberId,
    sport: plStatementSubGame(game, row.sport),
    game,
    marketId: row.marketId,
    eventId: row.eventId,
    eventName: row.eventName,
    marketName: row.marketName,
    memberPnl,
    commission,
    netAmount: -bookNet,
    split: amounts,
    betCount: row.betCount ?? 1,
    turnover: fromMinorUnits(row.turnoverMinor),
    memberName: row.name,
    splitNames: names,
  }
}

export function toPlStatementResult(
  live: AnalyticsPlStatementPage,
  query: PlStatementQuery,
): PlStatementResult {
  const pageSize = live.size ?? query.pageSize ?? DEFAULT_PAGE_SIZE
  const apiPage = live.page ?? 0
  const rows = (live.rows ?? []).map(toPlStatementRow)
  const splitRoles = (live.splitRoles ?? [])
    .map(asRole)
    .filter((role): role is Role => role !== null)
  return {
    rows,
    splitRoles:
      splitRoles.length > 0
        ? splitRoles
        : ROLES.filter((role) => rows.some((row) => typeof row.split[role] === 'number')),
    total: live.total ?? live.rows?.length ?? 0,
    page: apiPage + 1,
    pageSize,
    netPnl: fromMinorUnitsSigned(live.netMinor),
    asOf: live.asOf,
    martStale: live.martStale,
  }
}

export type PnlSummary = {
  rows: PnlRow[]
  totals: { netPnl: number }
  asOf?: string
  martStale?: boolean
}

export function fromFixturePnl(
  summary: PnlSummary,
  query: PlStatementQuery,
  memberName: (id: string) => string,
): PlStatementResult {
  const needle = query.q?.trim().toLowerCase() ?? ''
  const filtered = summary.rows.filter((row) => {
    if (query.subGame && row.sport !== query.subGame) return false
    if (query.eventName && row.eventName !== query.eventName) return false
    if (!needle) return true
    return (
      row.eventName.toLowerCase().includes(needle) ||
      row.marketName.toLowerCase().includes(needle) ||
      memberName(row.memberId).toLowerCase().includes(needle)
    )
  })

  const pageSize = query.pageSize ?? DEFAULT_PAGE_SIZE
  const total = filtered.length
  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1)
  const page = Math.min(Math.max(1, query.page ?? 1), pageCount)
  const start = (page - 1) * pageSize
  const rows: PlStatementRowView[] = filtered.slice(start, start + pageSize).map((row) => ({
    ...row,
    marketId: row.marketId ?? row.eventId,
    memberName: memberName(row.memberId),
    splitNames: {},
  }))

  const netPnl =
    needle || query.subGame || query.eventName
      ? filtered.reduce((sum, row) => sum - row.netAmount, 0)
      : summary.totals.netPnl

  return {
    rows,
    splitRoles: ROLES.filter((role) =>
      filtered.some((row) => typeof row.split[role] === 'number'),
    ),
    total,
    page,
    pageSize,
    netPnl,
    asOf: summary.asOf,
    martStale: summary.martStale,
  }
}

export function fixtureEventsForPll(
  rows: Array<{ eventName: string; sport: string }>,
  query: Pick<PlStatementQuery, 'subGame'>,
): string[] {
  const names = new Set<string>()
  for (const row of rows) {
    if (query.subGame && row.sport !== query.subGame) continue
    if (row.eventName) names.add(row.eventName)
  }
  return [...names].sort((a, b) => a.localeCompare(b))
}
